import React from 'react';
import { SupportedLocale } from '../lib/content';

type Props = {
  locale?: SupportedLocale;
};

export const SporyZBankamiHub: React.FC<Props> = ({ locale = 'pl' }) => {
  const isEn = locale === 'en';
  const isEs = locale === 'es';

  const title = isEn
    ? 'Borrower disputes with banks'
    : isEs
    ? 'Disputas de prestatarios con bancos'
    : 'Spory kredytobiorców z bankami';

  const intro = isEn
    ? 'Choose the case you need help with'
    : isEs
    ? 'Elige el asunto en el que necesitas ayuda'
    : 'Wybierz sprawę, w której potrzebujesz pomocy';

  const items: Array<{
    title: string;
    desc: string;
    href: string;
    ctaLabel?: string;
    ctaHref?: string;
  }> = [
    {
      title: isEn ? 'Free-credit sanction (SKD)' : isEs ? 'Sanción de crédito gratuito (SKD)' : 'Sankcja kredytu darmowego (SKD)',
      desc: isEn
        ? 'Do you have a consumer credit agreement but its terms raise doubts? Do you want to know whether your agreement is covered by the free-credit sanction (repayment without interest and costs) and, if so, how to use it?'
        : isEs
        ? '¿Tienes un contrato de crédito al consumo pero sus cláusulas te generan dudas? ¿Quieres saber si tu contrato está cubierto por la sanción de crédito gratuito (pago sin intereses y costes) y, si es así, cómo utilizarla?'
        : 'Masz umowę o kredyt konsumencki, ale postanowienia tej umowy budzą Twoje wątpliwości? Chcesz wiedzieć, czy Twoja umowa jest objęta sankcją kredytu darmowego, która oznacza, że spłacasz kredyt bez odsetek i kosztów, a jeśli tak, to jak skorzystać z tej sankcji?',
      href: '/spory-kredytobiorcow-z-bankami/skd',
      ctaLabel: isEn ? 'See how we help' : isEs ? 'Ver cómo ayudamos' : 'Sprawdź, jak pomagamy',
      ctaHref: '/spory-kredytobiorcow-z-bankami/skd',
    },
    {
      title: isEn ? 'CHF-linked mortgages (“franc loans”)' : isEs ? 'Créditos “francos” (CHF)' : 'Kredyty "frankowe"',
      desc: isEn
        ? 'Do you have a mortgage agreement indexed to a foreign currency (e.g. CHF, EUR), repaid in that currency or in PLN? Do you want to know whether the agreement is valid, whether you can demand reimbursement of amounts paid under it and remove the mortgage entry — and if so, how to do it?'
        : isEs
        ? '¿Tienes un contrato hipotecario indexado a divisa extranjera (p. ej., CHF, EUR), pagado en esa divisa o en PLN? ¿Quieres saber si el contrato es válido, si puedes reclamar la devolución de cantidades pagadas y cancelar la hipoteca, y cómo hacerlo?'
        : 'Masz umowę o kredyt hipoteczny, waloryzowaną do waluty obcej (np. do CHF, EUR), spłacaną w tej walucie lub w złotówkach? Chcesz wiedzieć, czy ta umowa jest ważna, czy możesz żądać od banku zwrotu świadczeń uiszczonych w wykonaniu tej umowy oraz wykreślić hipotekę, a jeśli tak, to jak to zrobić? ',
      href: '/spory-kredytobiorcow-z-bankami/franki',
      ctaLabel: isEn ? 'See how we help' : isEs ? 'Ver cómo ayudamos' : 'Sprawdź, jak pomagamy',
      ctaHref: '/spory-kredytobiorcow-z-bankami/franki',
    },
    {
      title: 'WIBOR',
      desc: isEn
        ? 'Do you have a WIBOR-based agreement but its terms raise doubts? Do you want to know whether the agreement is valid and how to seek reimbursement of amounts you have paid?'
        : isEs
        ? '¿Tienes un contrato basado en WIBOR pero sus cláusulas te generan dudas? ¿Quieres saber si el contrato es válido y cómo reclamar la devolución de cantidades pagadas?'
        : 'Masz umowę waloryzowaną WIBOR-em, ale postanowienia tej umowy budzą Twoje wątpliwości? Chcesz wiedzieć, czy ta umowa jest ważna oraz jak dochodzić zwrotu uiszczonych przez Ciebie świadczeń?',
      href: '/spory-kredytobiorcow-z-bankami/wibor',
      ctaLabel: isEn ? 'See how we help' : isEs ? 'Ver cómo ayudamos' : 'Sprawdź, jak pomagamy',
      ctaHref: '/spory-kredytobiorcow-z-bankami/wibor',
    },
    {
      title: isEn ? 'Other bank disputes' : isEs ? 'Otros conflictos con bancos' : 'Inne spory z bankami',
      desc: isEn
        ? 'Do you have a dispute with a bank that does not fit the other categories? Describe your situation briefly (a few sentences) and we will come back with a proposed course of action.'
        : isEs
        ? '¿Tienes un conflicto con un banco que no encaja en las otras categorías? Describe brevemente tu situación (unas frases) y volveremos con una propuesta de actuación.'
        : 'Masz spór z bankiem, który nie pasuje do pozostałych kategorii? Opisz swoją sytuację (krótko, w kilku zdaniach), a my wrócimy do Ciebie z propozycją działania.',
      href: '/#kontakt',
      ctaLabel: isEn ? 'Describe your bank dispute' : isEs ? 'Describe tu conflicto' : 'Opisz nam swój spór z bankiem',
      ctaHref: '/#kontakt',
    },
  ];

  const fallbackCta = isEn ? 'Contact us' : isEs ? 'Contacta con nosotros' : 'Skontaktuj się';

  return (
    <main style={{ paddingTop: 28, paddingBottom: 44 }}>
      <div className="container">
        <h2 className="section-heading" style={{ margin: 0 }}>
          {title}
        </h2>
        <p className="muted" style={{ marginTop: 10, maxWidth: 860 }}>
          {intro}
        </p>

        <div className="forwhom-grid" style={{ marginTop: 18 }}>
          {items.map((it) => (
            <div className="card" key={it.title}>
              <h3 style={{ marginTop: 0 }}>{it.title}</h3>

              <p className="muted" style={{ margin: 0 }}>
                {it.desc}
              </p>

              <div style={{ marginTop: 12 }}>
                <a href={it.ctaHref || '/#kontakt'} className="link-button">
                  {it.ctaLabel || fallbackCta}
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </main>
  );
};